import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * An image shown full size over everything — a screenshot opened to be read.
 *
 * Portalled to the body and stacked above the modals, because it is always
 * opened FROM a modal. Escape, the close button or a click outside the image
 * closes it; a click on the image itself does not, so somebody reading a
 * detail is not thrown out by a stray click. An image larger than the screen
 * scrolls rather than being shrunk, since the point is to read it.
 */
export function ImageViewer({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    /* Capture, so the modal underneath does not also close on the same Escape. */
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <div className="image-viewer" role="dialog" aria-modal="true" aria-label={alt} onClick={onClose}>
      <button type="button" className="image-viewer-close" onClick={onClose} aria-label="Close">
        ×
      </button>
      <div className="image-viewer-frame">
        <img src={src} alt={alt} onClick={(e) => e.stopPropagation()} />
      </div>
    </div>,
    document.body,
  );
}
